import { ReactNode } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useSelector, shallowEqual } from 'react-redux'

import Link from 'app/common/lib/link'
import Restrictions from 'app/common/components/Restrictions'
import ChangePassword from 'app/common/components/ChangePassword'
import ChangeLogin from 'app/common/components/ChangeLogin'
import selectIsChangePassword from 'app/common/components/ChangePassword/selectIsChangePassword'
import selectIsChangeLogin from 'app/common/components/ChangeLogin/selectIsChangeLogin'
import Confirmation from 'app/common/components/Confirmation'
import Banner, { BannersRoot, BannerRefresher } from 'app/common/components-next/banner'
import FailureHandler from 'app/common/components/FailureHandler'
// import { CartInit } from 'app/common/components/Shop'
import SettingsDataProvider from 'app/common/components-next/settings-data'
import { useActivity, activityTypes } from 'app/common/components-next/activity'
import { useStatementsTitle } from 'app/pages/StatementsPage'
// import isProduction from 'app/common/lib/isProduction';
import { useUser, useUserJivoCode } from 'app/dataProviders/user/hooks'
import Person from 'app/common/classes/person'
import { useDetectOs } from 'app/common/hooks/useDetectOs'
import { Theme, getColorFromTheme } from 'app/common/components-next/color-theme'

import AppBar, {
  LeftBarContent,
  RightBarContent,
  MenuItems,
  MenuItem,
  MenuItemWithDropdown,
  MenuDropdownItem,
} from 'app/common/ui-components/app-bar'

import { RsnEmail, isRsnEmail, RsnDocument, isRsnDocument } from './restrictions'
import enhanceApp from './enhanceApp'
import Loader from '../Loader'
import Logo from './Logo'
import AnimatedFavorites from './AnimatedFavorites'
import UserMenuLocal from './UserMenuLocal'
// import HelpMenu from './HelpMenu';
// import ShopIcon from './ShopIcon';
import EventsIcon from './EventsIcon'
import AppFooter from './AppFooter'
import { useCheckStatementsEnabled } from '../Statements'
import { JivoWidget } from '../../common/ui-components/jivo/JivoWidget'

import styles from './App.scss'

type Props = {
  children?: ReactNode
  // refreshFromDisconnect: (void) => void,
  // mounted: boolean,
  isNeedRefresh: boolean
  user: any
  // disconnected: boolean,
  title: string
  location: Object
  // needNewPass: boolean,
  // needNewLogin: boolean,
}

const App = ({
  children,
  // mounted,
  isNeedRefresh,
  user,
  // disconnected,
  // refreshFromDisconnect,
  title,
  location,
}: Props) => {
  const { pathname } = window.location
  const { t } = useTranslation('app')
  const currentStudentId = useSelector((state: any) => state.settings.current.id, shallowEqual)
  // const shopEnabled = useSelector((state: any) => state.user.shop.enabled, shallowEqual);

  const [statementTitle, loadingStatementTitle] = useStatementsTitle()
  const enableStatements = useCheckStatementsEnabled(loadingStatementTitle)
  const enableHelp = false // !isProduction();

  useActivity(activityTypes.ACE_FIRST_ACCESS, pathname)

  const {
    name,
    type,
    phone,
    email,
    balance: { primaryBalanceHidden },
    loading: loadingUser,
  } = useUser()

  const os = useDetectOs()
  const jivoCode = useUserJivoCode()

  const isUserStudent = type === 'student'

  return (
    <div className={styles.root}>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <JivoWidget
        jivoCode={jivoCode}
        name={new Person(name).getName().LastFirst || 'n/a'}
        phone={phone || 'n/a'}
        email={email || 'n/a'}
        role={{ parent: 'Родитель', student: 'Учащийся', employee: 'Сотрудник' }[type] || 'n/a'}
        os={os || 'n/a'}
        themeColor={getColorFromTheme(Theme.primaryColor)}
        ready={os !== 'Loading' && !!jivoCode && !loadingUser}
      />

      <SettingsDataProvider root="common">
        {/* <CartInit pathname={pathname}> */}
        <AppBar>
          <LeftBarContent>
            <Logo />
            <MenuItems>
              <MenuItem
                to="/children"
                active={/^\/(children)(\/|$)/.test(pathname)}
                // onClick={refresh}
              >
                {isUserStudent ? t('app_main_menu_item1_1') : t('app_main_menu_item1_2')}
              </MenuItem>
              <MenuItem to="/apps" active={/^\/(apps|app)(\/|$)/.test(pathname)}>
                {t('app_main_menu_item2')}
              </MenuItem>
              {!isUserStudent && !primaryBalanceHidden && (
                <MenuItem to="/finances" active={/^\/(finances)(\/|$)/.test(pathname)}>
                  {t('app_main_menu_item3')}
                </MenuItem>
              )}
              <MenuItem
                to={currentStudentId ? `/identifiers/${currentStudentId}` : '/identifiers'}
                active={/^\/(identifiers)(\/|$)/.test(pathname)}
              >
                {t('app_main_menu_item4')}
              </MenuItem>
              {/* {shopEnabled
                && <MenuItem
                  to="/shop"
                  active={/^\/(shop)(\/|$)/.test(pathname)}
                >
                  {t('app_main_menu_item5')}
                </MenuItem>} */}

              {enableStatements && (
                <MenuItem to="/statements" active={/^\/(statements)(\/|$)/.test(pathname)}>
                  {statementTitle}
                </MenuItem>
              )}

              {enableHelp && (
                <MenuItemWithDropdown
                  id="more"
                  title={t('app_main_menu_more')}
                  active={/^\/(assistance)(\/|$)/.test(pathname)}
                >
                  {/* {enableStatements
                    && <MenuDropdownItem>
                      <Link to="/statements">{statementTitle}</Link>
                    </MenuDropdownItem>} */}
                  {enableHelp && (
                    <MenuDropdownItem>
                      <Link to="/assistance/ask">{t('app_main_menu_item7')}</Link>
                    </MenuDropdownItem>
                  )}
                </MenuItemWithDropdown>
              )}
            </MenuItems>
          </LeftBarContent>
          <RightBarContent>
            <EventsIcon pathname={pathname} />
            {/* <ShopIcon pathname={location.pathname} /> */}
            {/* {!isProduction() && <HelpMenu />} */}

            <UserMenuLocal />
          </RightBarContent>
        </AppBar>

        <AnimatedFavorites studentID={String(currentStudentId)} />

        {/* @ts-ignore */}
        <BannersRoot>
          <BannerRefresher pathname={pathname} />
          <Banner placeId="f5992ca8-9888-4152-ae80-32cc6a429eae" className={styles.banner} />

          {/* ОСНОВНОЙ КОНТЕНТ */}
          <section className={styles.body}>
            {!isNeedRefresh && user.result === 'success' ? children : <Loader />}
          </section>
        </BannersRoot>

        {/* </CartInit> */}
      </SettingsDataProvider>

      <AppFooter />

      <Confirmation />
      <Restrictions
        location={location}
        restrictions={[
          {
            condition: selectIsChangeLogin,
            component: ChangeLogin,
          },
          {
            condition: selectIsChangePassword,
            component: ChangePassword,
          },
          {
            condition: isRsnEmail,
            component: RsnEmail,
          },
          {
            condition: isRsnDocument,
            component: RsnDocument,
          },
        ]}
      />
      <FailureHandler />
    </div>
  )
}

export default enhanceApp(App)
